import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined'
import CloseOutlined from '@mui/icons-material/CloseOutlined'
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined'
import Stack from '@mui/material/Stack'
import {
  closeSnackbar,
  type CustomContentProps,
  type SnackbarAction,
} from 'notistack'
import { forwardRef, useEffect } from 'react'

import { StyledSnackbarContent, type Variant } from './Snackbar.style'
import { TextArea } from '../../data-display/calendar/components/slots/Slot/TextArea/TextArea'
import { IconButton } from '../../inputs/icon-button/IconButton'

export const Snackbar = forwardRef<
  HTMLDivElement,
  Omit<CustomContentProps, 'variant'> & { variant: Variant }
>(({ variant, action, message, id }, ref) => {
  const icons = {
    success: <CheckCircleOutlined color="success" />,
    warning: <ReportProblemOutlined color="warning" />,
    error: <ErrorOutlineOutlined color="error" />,
    info: <InfoOutlined color="info" />,
  }

  const onClickDismiss = () => {
    closeSnackbar(id)
  }

  // A toast isn't focusable, so wire Escape at the document level to let
  // keyboard users dismiss it (Notion a11y spec).
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeSnackbar(id)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [id])

  const Action: SnackbarAction =
    typeof action === 'function' ? action(id) : action

  return (
    <StyledSnackbarContent
      ref={ref}
      type={variant}
      role="alert"
      message={
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Stack>{icons[variant]}</Stack>
          <TextArea sx={{ flex: 1 }}>{message}</TextArea>
          <Stack>{Action}</Stack>
          <IconButton ariaLabel="close" onClick={onClickDismiss} size="small">
            <CloseOutlined />
          </IconButton>
        </Stack>
      }
    />
  )
})
